import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import ListPatientTest from "./ListPatientTest";

const PatientTest = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data: patients, isLoading: loadingPatients } = useFetchObjects(
    ["patient"],
    "patients",
    token
  );
  const { data: tests } = useFetchObjects(
    ["laboratory-tests"],
    "laboratory-tests",
    token
  );
  const { data: doctors, isLoading: loadingDoctors } = useFetchObjects(
    ["users"],
    `users/?position=11`,
    token
  );

  const [orderData, setOrderData] = useState({
    date: new Date().toISOString().split("T")[0],
    doctor: auth?.user?.user?.id || "",
    patient: "",
    urgency: false,
    discount: 0,
    tests: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedTests, setSelectedTests] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredTests, setFilteredTests] = useState([]);

  const { handleAdd, loading, resetForm } = useAdd(
    "laboratory-test-orders",
    token
  );

  // Sort patients by creation date (newest to oldest)
  const sortedPatients = patients?.results?.slice().sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA; // Newest first
  });

  useEffect(() => {
    if (resetForm) {
      setOrderData({
        date: new Date().toISOString().split("T")[0],
        doctor: auth?.user?.user?.id || "",
        patient: "",
        urgency: false,
        discount: 0,
        tests: [],
      });
      setSelectedTests([]);
      setFormErrors({});
    }
  }, [resetForm]);

  useEffect(() => {
    if (tests && searchText) {
      const filtered = tests.filter((test) =>
        test.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredTests(filtered);
    } else {
      setFilteredTests([]);
    }
  }, [searchText, tests]);

  const handleAddNewTest = (test) => {
    if (test) {
      const allTests = test.sub_tests ? [test, ...test.sub_tests] : [test];
      setSelectedTests((prevTests) => [...prevTests, ...allTests]);
      setSearchText("");
    }
  };

  const handleRemoveTest = (index) => {
    const updatedTests = [...selectedTests];
    updatedTests.splice(index, 1);
    setSelectedTests(updatedTests);
  };

  const handleChangeField = (e) => {
    const { name, value } = e.target;
    setOrderData({ ...orderData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!orderData.patient) {
      setFormErrors({ patient: t("laboratory.patientRequired") });
      return;
    }
    if (!orderData.doctor) {
      setFormErrors({ doctor: t("laboratory.doctorRequired") });
      return;
    }
    handleAdd({
      ...orderData,
      tests: selectedTests.map((test) => ({ test_type: test.id })),
      discount: parseFloat(orderData.discount) || 0,
    });
  };

  const calculateTotal = () => {
    let totalCost = 0;

    selectedTests.forEach((test) => {
      if (test.is_parent_test) {
        // Parent test logic
        if (test.cost) {
          totalCost += parseFloat(test.cost); // Use parent test price if it exists
        } else if (test.child_tests?.length) {
          // If no parent price, calculate the total from child test prices
          test.child_tests.forEach((child) => {
            if (child.cost) {
              totalCost += parseFloat(child.cost);
            }
          });
        }
      } else {
        // Standalone test logic (child tests selected directly)
        if (test.cost) {
          totalCost += parseFloat(test.cost);
        }
      }
    });

    const discount = parseFloat(orderData.discount) || 0;
    return Math.max(0, totalCost - discount); // Ensure the total cannot be negative
  };

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {t("laboratory.addPatientTestsTitle")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("laboratory.patientInfo")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("laboratory.orderDate")}
                name="date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={orderData.date}
                onChange={handleChangeField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {loadingPatients ? (
                <CircularProgress size={24} />
              ) : (
                <Autocomplete
                  options={sortedPatients || []} // Use sorted patients
                  getOptionLabel={(option) =>
                    `${option.firstname} ${option.lastname} (${option.id})`
                  }
                  onChange={(event, value) =>
                    setOrderData({
                      ...orderData,
                      patient: value ? value.id : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("laboratory.patient")}
                      placeholder={t("laboratory.searchByIdOrName")}
                      error={!!formErrors.patient}
                      helperText={formErrors.patient}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {loadingDoctors ? (
                <CircularProgress size={24} />
              ) : (
                <Autocomplete
                  options={doctors || []}
                  getOptionLabel={(option) => `${option.username}`}
                  value={
                    doctors.find((doctor) => doctor.id === orderData.doctor) ||
                    null
                  }
                  onChange={(event, value) =>
                    setOrderData({
                      ...orderData,
                      doctor: value ? value.id : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("laboratory.doctor")}
                      error={!!formErrors.doctor}
                      helperText={formErrors.doctor}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("laboratory.addTest")}
          </Typography>
          <Autocomplete
            value={null}
            inputValue={searchText}
            onInputChange={(event, newInputValue) =>
              setSearchText(newInputValue)
            }
            onChange={(event, value) => handleAddNewTest(value)}
            options={filteredTests}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("laboratory.searchTest")}
                placeholder={t("laboratory.searchTestPlaceholder")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            )}
          />
        </Box>

        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>{t("laboratory.testName")}</TableCell>
                <TableCell align="center">{t("laboratory.testCost")}</TableCell>
                <TableCell align="center">{t("laboratory.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedTests.map((test, index) => (
                <TableRow key={index} hover>
                  <TableCell>{test.name}</TableCell>
                  <TableCell align="center">{test.cost || "N/A"}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("laboratory.removeTest")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveTest(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <TextField
            label={t("laboratory.discount")}
            name="discount"
            type="number"
            value={orderData.discount}
            onChange={handleChangeField}
            sx={{
              width: "30%",
              backgroundColor: "#fff",
              borderRadius: 2,
            }}
          />
          <Typography variant="h6" fontWeight={600}>
            {t("laboratory.totalAmount")}: {calculateTotal()}؋
          </Typography>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            size="small"
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: 700,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? t("laboratory.saving") : t("laboratory.saveOrder")}
          </Button>
        </Stack>
      </CardContent>
      <Divider />
      <Box m={3}>
        <ListPatientTest />
      </Box>
    </Card>
  );
};

export default PatientTest;
